import {API} from '../common/axios'
import {webEvents} from '../common/WebEvents'

export const ITER_NAMES = [
    null,
    'First',
    'Second',
    'Third',
    'Fourth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth',
    'Ninth',
    'Tenth',
    'Eleventh',
    'Twelfth',
    'Thirteenth',
    'Fourteenth',
    'Fifteenth',
    'Sixteenth',
    'Seventeenth',
    'Eighteenth',
    'Nineteenth',
    'Twentieth',
    'Twenty-first',
    'Twenty-second',
    'Twenty-third',
    'Twenty-fourth',
    'Twenty-fifth',
    'Twenty-sixth',
    'Twenty-seventh',
    'Twenty-eighth',
    'Twenty-ninth'
]

export const MAP_KEY_SEP_CHAR = '\uff0c'

/* This is a mix of count and iteration properties. Counts can have lots of iterations. A full Count can take up
 * a large amount of memory, which is wasteful because we get our report items from saveReport.
 */
export type ActiveIteration = {
    countId: number // from count
    iterationId: number
    type: string // from count
    status: string
    sample: Sample[] | null
    description: string // from count
    assignedAt: Date | null
    requestedAt: Date
    requestedBy: string
}

export type ReportItem = {
    sku: string
    quantity: number
    serial: string | null
    bin: string | null
    enteredAt: string
}

export type SaveInventoryItem = ReportItem & {
    excluded: string
    progress?: string
}

export type SaveInventoryRequestItem = ReportItem & {
    excluded?: string
    op: string
}

export type SaveReportItemMap = {[key: string]: SaveInventoryItem}

export type ExcludedItem = {
    serial: string | null
    locationId: string | null
    sku: string
    bin: string | null
    status: string
    transferOrderId: number | null
    transferReason: string | null
    details: object | null
    excluded: boolean
}

export type Discrepancy = {
    sku: string
    expected: number
    counted: number
    serials: string[]
    cost: number
}

export type Sample = {
    sku: string
    serialized: boolean
}

export type ProductSample = {
    sku: string
    serialized: boolean
    bin: string
    modelNumber: string
}

export type Comment = {message: string; postedAt: Date}

/* excludes properties we don't need */
export type Iteration = {
    iterationId: number
    status: string
    sample: Sample[] | null
    comments: Comment[]
    report: ReportItem[]
    excludedItems: ExcludedItem[]
    discrepancies: Discrepancy[]
    requestedAt: Date
    requestedBy: string
    assignedAt: Date | null
    assignedTo: string | null
    savedAt: Date | null
    submittedAt: Date | null
    reviewedAt: Date | null
    reviewedBy: string | null
}

export type Count = {
    countId: number
    locationId: string | null
    type: string
    status: string
    description: string
    iterations: Iteration[]
    requestedAt: Date
    requestedBy: string
    openedAt: Date | null
    openedBy: string | null
    closedAt: Date | null
    closedBy: string | null
    createdAt: Date
}

export type IterationResult = {
    iterationId: number
    status: string
    sample: Sample[] | null
    comments: Comment[]
    requestedAt: Date
    requestedBy: string
    assignedAt: Date | null
    assignedTo: string | null
    savedAt: Date | null
    submittedAt: Date | null
    reviewedAt: Date | null
    reviewedBy: string | null,
    reviewStats: {
        totalGainedValue: number
        totalLostValue: number
        totalUnexpectedQuantity: number
        totalMissingQuantity: number
    }
}

export type CountResult = {
    countId: number
    locationId: string | null
    type: string
    status: string
    description: string
    iteration: IterationResult
    requestedAt: Date
    requestedBy: string
    openedAt: Date | null
    openedBy: string | null
    closedAt: Date | null
    closedBy: string | null
    createdAt: Date
}

const toDate = (value: string | null) => (value && new Date(value)) || null

export const getCount = async (countId: number): Promise<Count> => {
    let response = await API.get(`inventory/counts/${countId}`)
    let c = response.data
    return {
        countId: c.count_id,
        locationId: c.location_id,
        type: c.type,
        status: c.status,
        description: c.description,
        requestedAt: new Date(c.requested_at),
        requestedBy: c.requested_by,
        openedAt: toDate(c.opened_at),
        openedBy: c.opened_by,
        closedAt: toDate(c.closed_at),
        closedBy: c.closed_by,
        createdAt: new Date(c.created_at),
        iterations: c.iterations.map(
            (iter: any): Iteration => ({
                iterationId: iter.iteration_id,
                status: iter.status,
                sample: iter.sample?.map((x: any): Sample => ({
                    sku: x.sku,
                    serialized: x.serialized
                })) ?? null,
                comments: iter.comments.map((x: any): Comment => ({
                    message: x.message,
                    postedAt: new Date(x.posted_at)
                })),
                report: iter.report.map((x: any): ReportItem => ({
                    bin: x.bin,
                    enteredAt: x.entered_at,
                    sku: x.sku,
                    quantity: x.quantity,
                    serial: x.serial
                })),
                excludedItems: iter.excluded_items.map((x: any): ExcludedItem => ({
                    serial: x.serial,
                    locationId: x.location_id,
                    sku: x.sku,
                    bin: x.bin,
                    status: x.status,
                    transferOrderId: x.transfer_order_id,
                    transferReason: x.transfer_reason,
                    details: x.details,
                    excluded: x.excluded
                })),
                discrepancies: iter.discrepancies.map((x: any): Discrepancy => ({
                    sku: x.sku,
                    expected: x.expected,
                    counted: x.counted,
                    serials: x.serials,
                    cost: x.cost
                })),
                requestedAt: new Date(iter.requested_at),
                requestedBy: iter.requested_by,
                assignedAt: toDate(iter.assigned_at),
                assignedTo: iter.assigned_to,
                savedAt: toDate(iter.saved_at),
                submittedAt: toDate(iter.submitted_at),
                reviewedAt: toDate(iter.reviewed_at),
                reviewedBy: iter.reviewed_by
            })
        )
    }
}

export const searchCounts = async (params: any): Promise<CountResult[]> => {
    let response = await API.get('inventory/counts', {params})

    return response.data.map(
        (c: any): CountResult => ({
            countId: c.count_id,
            locationId: c.location_id,
            type: c.type,
            status: c.status,
            description: c.description,
            requestedAt: new Date(c.requested_at),
            requestedBy: c.requested_by,
            openedAt: toDate(c.opened_at),
            openedBy: c.opened_by,
            closedAt: toDate(c.closed_at),
            closedBy: c.closed_by,
            createdAt: new Date(c.created_at),
            iteration: {
                iterationId: c.iteration.iteration_id,
                status: c.iteration.status,
                sample: c.iteration.sample?.map((x: any): Sample => ({
                    sku: x.sku,
                    serialized: x.serialized
                })) ?? null,
                comments: c.iteration.comments.map((x: any): Comment => ({
                    message: x.message,
                    postedAt: new Date(x.posted_at)
                })),
                requestedAt: new Date(c.iteration.requested_at),
                requestedBy: c.iteration.requested_by,
                assignedAt: toDate(c.iteration.assigned_at),
                assignedTo: c.iteration.assigned_to,
                savedAt: toDate(c.iteration.saved_at),
                submittedAt: toDate(c.iteration.submitted_at),
                reviewedAt: toDate(c.iteration.reviewed_at),
                reviewedBy: c.iteration.reviewed_by,
                reviewStats: {
                    totalGainedValue: c.iteration.review_stats.total_gained_value,
                    totalLostValue: c.iteration.review_stats.total_lost_value,
                    totalMissingQuantity: c.iteration.review_stats.total_missing_quantity,
                    totalUnexpectedQuantity: c.iteration.review_stats.total_unexpected_quantity
                }
            }
        })
    )
}

function toReportPayload(items: SaveInventoryRequestItem[] | null): any | undefined {
    /* when "items" is null, payload will be undefined, which is desired */
    return items?.map((x: SaveInventoryRequestItem) => {
        let item: any = {op: x.op, entered_at: x.enteredAt}

        if (x.bin)
            item.bin = x.bin

        if (x.serial) {
            item.serial = x.serial
        } else {
            item.sku = x.sku
            item.quantity = x.quantity
        }

        return item
    })
}

export const saveReport = async (
    count_id: number,
    items: SaveInventoryRequestItem[] | null
): Promise<SaveInventoryItem[]> => {
    let resp = await API.post(`inventory/counts/${count_id}/save-report`, toReportPayload(items))

    return resp.data.map((x: any[]): SaveInventoryItem => ({
        serial: x.shift(),
        sku: x.shift(),
        quantity: x.shift(),
        bin: x.shift(),
        enteredAt: new Date(x.shift()).toISOString(),
        excluded: x.shift()
    }))
}

export const saveReceipt = async (
    transfer_order_id: number,
    items: SaveInventoryRequestItem[] | null
): Promise<SaveInventoryItem[]> => {
    let resp = await API.post(`inventory/transfers/${transfer_order_id}/save-receipt`, toReportPayload(items))

    return resp.data.map((x: any[]): SaveInventoryItem => ({
        serial: x.shift(),
        sku: x.shift(),
        quantity: x.shift(),
        bin: null,
        enteredAt: new Date(x.shift()).toISOString(),
        excluded: x.shift()
    }))


}

export const submitReport = async (count_id: number) => {
    await API.post(`inventory/counts/${count_id}/submit-report`)
}

export const fetchBins = async () => {
    const response = await API.get('inventory/products/bins')
    return response.data
}

export const processSaveReportResponse = (
    respItems: SaveInventoryItem[],
    serialItems: SaveInventoryItem[],
    nonSerialItems: SaveInventoryItem[],
    isSync: boolean
) => {
    if (isSync) {
        let serials = []
        let nonSerials = []

        for (let item of respItems) {
            if (item.serial)
                serials.push(item)
            else
                nonSerials.push(item)
        }

        /* add queue items */
        serials.push(...serialItems.filter(x => x.progress?.startsWith('q-')))
        nonSerials.push(...nonSerialItems.filter(x => x.progress?.startsWith('q-')))

        return {serials, nonSerials}
    }

    let serialsChanged = false
    let nonSerialsChanged = false

    for (let item of respItems) {
        let items = item.serial ? serialItems : nonSerialItems
        let index = items.findIndex(x => x.enteredAt === item.enteredAt)

        if (index !== -1) {
            if (item.serial)
                serialsChanged = true
            else
                nonSerialsChanged = true

            /* server returns -1 quantity when the item was deleted */
            if (item.quantity < 0) {
                items.splice(index, 1)
            } else {
                delete items[index].progress
                items[index].quantity = item.quantity
                items[index].serial = item.serial
                items[index].bin = item.bin
                items[index].enteredAt = item.enteredAt
                items[index].sku = item.sku
                items[index].excluded = item.excluded
            }
        }
    }

    return {
        serials: serialsChanged ? [...serialItems] : null,
        nonSerials: nonSerialsChanged ? [...nonSerialItems] : null
    }
}

export const flushInventoryItemsQueue = (
    serialItems: SaveInventoryItem[],
    nonSerialItems: SaveInventoryItem[]
) => {
    let serialsQueued = serialItems.filter(x => x.progress?.startsWith('q-'))
    let nonSerialsQueued = nonSerialItems.filter(x => x.progress?.startsWith('q-'))

    const getProgress = (x: SaveInventoryItem) => {
        if (x.progress === 'q-delete')
            return 'deleting'
        if (x.progress === 'q-update')
            return 'updating'
        return 'adding'
    }

    serialsQueued.forEach(getProgress)
    nonSerialsQueued.forEach(getProgress)

    const itemToRequest = (x: SaveInventoryItem): SaveInventoryRequestItem => ({
        op: x.progress === 'deleting' || x.progress === 'q-delete' ? 'delete' : 'add',
        sku: x.sku,
        serial: x.serial,
        bin: x.bin,
        quantity: x.quantity,
        enteredAt: x.enteredAt,
        excluded: x.excluded
    })

    const clearAddingProgress = (item: SaveInventoryItem) => {
        if (item.progress === 'adding') {
            let alreadyExists = false

            if (item.serial) {
                alreadyExists = serialItems.some(existingItem =>
                    existingItem.sku === item.sku && existingItem.serial === item.serial
                )
            } else {
                alreadyExists = nonSerialItems.some(existingItem =>
                    existingItem.sku === item.sku
                )
            }

            if (alreadyExists) {
                serialItems.splice(serialItems.indexOf(item), 1)
            }
        }
    }

    serialItems.forEach(clearAddingProgress)
    nonSerialItems.forEach(clearAddingProgress)

    let requestItems: SaveInventoryRequestItem[] = [
        ...serialsQueued.map(itemToRequest),
        ...nonSerialsQueued.map(itemToRequest)
    ]

    return {
        requestItems: requestItems.length ? requestItems : null,
        serials: serialsQueued.length ? [...serialItems] : null,
        nonSerials: nonSerialsQueued.length ? [...nonSerialItems] : null
    }
}

export const processFailedSaveResponse = (
    serialItems: SaveInventoryItem[],
    nonSerialItems: SaveInventoryItem[]
) => {
    const updatedSerialItems = serialItems.filter(item => {
        if (item.progress === 'adding') {
            return false
        }
        if (item.progress === 'deleting') {
            item.progress = undefined
        }
        return true
    })

    const updatedNonSerialItems = nonSerialItems.filter(item => {
        if (item.progress === 'adding') {
            return false
        }
        if (item.progress === 'deleting' || item.progress === 'updating') {
            item.progress = undefined
        }
        return true
    })
    return {updatedSerialItems, updatedNonSerialItems}
}

export const processRequestItem = (
    reqItem: SaveInventoryRequestItem,
    serialItems: SaveInventoryItem[],
    nonSerialItems: SaveInventoryItem[],
    shouldQueue: boolean
): {
    progress: string,
    serials: SaveInventoryItem[] | null,
    nonSerials: SaveInventoryItem[] | null
} | null => {
    let result: any = {}

    /* When deleting, we need to modify the progress of an existing item. */
    if (reqItem.op === 'delete') {
        let currItem = (reqItem.serial ? serialItems : nonSerialItems).find(x => x.enteredAt === reqItem.enteredAt)

        if (!currItem) {
            webEvents.error(`Attempt to delete non-existent item`, {request_item: reqItem})
            return {progress: '', serials: null, nonSerials: null}
        }

        result.progress = currItem.progress = shouldQueue ? 'q-delete' : 'deleting'

        if (reqItem.serial)
            result.serials = [...serialItems]
        else
            result.nonSerials = [...nonSerialItems]
    } else if (reqItem.op === 'update') {
        if (reqItem.serial)
            throw new Error(`serialized items cannot perform updates`)

        let currItem = nonSerialItems.find(x => x.enteredAt === reqItem.enteredAt)
        if (!currItem || currItem.quantity === reqItem.quantity)
            return {progress: '', serials: null, nonSerials: null}

        /* modify the progress of existing item; although saved it has become dirty */
        reqItem.op = 'add' // server uses an UPSERT with non-serial adds, "update" op not needed
        result.progress = currItem.progress = shouldQueue ? 'q-update' : 'updating'
        result.nonSerials = [...nonSerialItems]
    } else {
        /* duplicate checks */
        if (reqItem.serial) {
            if (serialItems.findIndex(x => x.serial === reqItem.serial) !== -1)
                return null
        } else if (nonSerialItems.findIndex(x => x.sku === reqItem.sku && x.bin === reqItem.bin) !== -1) {
            return null
        }

        result.progress = shouldQueue ? 'q-add' : 'adding'

        /* add the new item to the list */
        let newItem: SaveInventoryItem = {
            sku: reqItem.sku,
            quantity: reqItem.quantity,
            serial: reqItem.serial,
            bin: reqItem.bin,
            progress: result.progress,
            enteredAt: reqItem.enteredAt,
            excluded: reqItem.excluded ?? ''
        }

        if (reqItem.serial)
            result.serials = [...serialItems, newItem]
        else
            result.nonSerials = [...nonSerialItems, newItem]
    }

    return result
}

export const parseBinString = (str: string) => {
    let parts = str.split(' ')
    let row = parts.length === 3 ? parts[1] : parts[0]
    let sleeveBin = parts[parts.length - 1].trim()
    let sleeve = sleeveBin.slice(0, 2)
    let bin = sleeveBin.slice(2)

    return {row, sleeve, bin}
}

export const isSpecialBin = (str: string): boolean =>
    str.startsWith('DRAWER') || str.startsWith('OVERSTOCK')


export const parseBinName = (str: string | null) => {
    if (!str) {
        return ''
    }
    const parts = str.split(' ')
    return parts.length === 1 ? parts[0] : parts.length === 2 ? `${parts[0]} ${parts[1]}` : `${parts[1]} ${parts[2]}`
}

export const fetchTrucks = async () => {
    const params = new URLSearchParams({
        types: 'TK',
        page_size: '200'
    })

    const response = await API.get(`inventory/locations`, {params})
    return response.data
}

export type SpotSample = {
    sku: string,
    model: string
}

export type CreateCount = {
    type: string,
    location_id: string,
    sample?: string[] | null,
    description?: string | null,
    on_behalf_of?: string
}

export type SearchUsersResult = {
    user_id: string,
    type?: string,
    truck?: string,
    roles?: string[],
    permissions?: string[],
    clocked_in?: string | null,
    full_name: string,
}

export type UserSearchParams = {
    users?: string,
    types?: string,
    trucks?: string,
    clocked_in?: boolean,
    banned?: boolean,
}

export const createCount = async (count: CreateCount) => {
    const response = await API.post('/inventory/counts', count)
    return response.data
}

export const approveReport = async (id: number, details: {
    sku: string,
    type: string | null
}[]) => {
    const response = await API.post(`inventory/counts/${id}/approve-report`, details)
    return response.data
}

export const closeCount = async (id: number) => {
    const response = await API.delete(`inventory/counts/${id}`)
    return response.data
}

// Returned dates are inclusive: timestamp >= startDate and timestamp <= endDate. mimics SQL BETWEEN behavior.
export const getCurrentQuarterDateRange = () => {
    const now = new Date()
    const year = now.getUTCFullYear()
    const month = now.getUTCMonth() + 1 // 0-based index (0 = January, 11 = December)

    let quarterStartDate
    let quarterEndDate
    let endTime = '23:59:59.999Z'

    if (month >= 10) { // October to December
        quarterStartDate = `${year}-10-01Z`
        quarterEndDate = `${year}-12-31T${endTime}`
    } else if (month >= 7) { // July to September
        quarterStartDate = `${year}-07-01Z`
        quarterEndDate = `${year}-09-30T${endTime}`
    } else if (month >= 4) { // April to June
        quarterStartDate = `${year}-04-01Z`
        quarterEndDate = `${year}-06-30T${endTime}`
    } else { // January to March
        quarterStartDate = `${year}-01-01Z`
        quarterEndDate = `${year}-03-31T${endTime}`
    }

    return {
        quarterStartDate,
        quarterEndDate
    }
}

export const getCurrentQuarterAndYear = () => {
    const {quarterStartDate} = getCurrentQuarterDateRange()
    const year = parseInt(quarterStartDate.slice(0, 4), 10)

    let quarter = 1
    const month = parseInt(quarterStartDate.slice(5, 7), 10)

    if (month === 10) {
        quarter = 4
    } else if (month === 7) {
        quarter = 3
    } else if (month === 4) {
        quarter = 2
    } else {
        quarter = 1
    }

    return {quarter, year}
}

export const getQuarterDateRange = (quarter: number, year: number) => {
    let quarterStartDate, quarterEndDate
    const endTime = '23:59:59.999Z'

    switch (quarter) {
        case 1:
            quarterStartDate = `${year}-01-01Z`
            quarterEndDate = `${year}-03-31T${endTime}`
            break
        case 2:
            quarterStartDate = `${year}-04-01Z`
            quarterEndDate = `${year}-06-30T${endTime}`
            break
        case 3:
            quarterStartDate = `${year}-07-01Z`
            quarterEndDate = `${year}-09-30T${endTime}`
            break
        case 4:
            quarterStartDate = `${year}-10-01Z`
            quarterEndDate = `${year}-12-31T${endTime}`
            break
        default:
            throw new Error('Invalid quarter')
    }

    return {quarterStartDate, quarterEndDate}
}

export const getName = (email: string | null) => {
    if (!email) return ''
    const username = email.split('@')[0].split('.')

    const firstName = username[0]
    const lastName = username.length > 1 ? username[1] : '' // Default to empty if no last name

    const capitalize = (name: string) => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()

    return `${capitalize(firstName)} ${capitalize(lastName)}`.trim()
}
